import {
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { readChallenges, readContentHealth, toggleChallenge } from '../api/endpoints';
import { useAdminMutation } from '../api/useAdminMutation';
import type { ChallengeDefinition, ChallengeHealth, ChallengeHealthRow } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ChallengeDashboardDialog } from '../components/ChallengeDashboardDialog';
import { EmptyState } from '../components/EmptyState';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageHeader } from '../components/PageHeader';
import { SortableCell } from '../components/SortableCell';
import { TableFilters } from '../components/TableFilters';
import { TableSkeletonRows } from '../components/TableSkeletonRows';
import { sortRows, useSort } from '../components/useSort';

const SKILL_LABEL: Record<string, string> = {
  SPATIAL: 'priestor',
  MEMORY: 'pamäť',
  LOGIC: 'logika',
  READING: 'čítanie',
  EXPRESSION: 'vyjadrovanie',
};

/**
 * Accuracy is read as a warning below this. A threshold for *looking*, not a verdict — a hard task is
 * allowed to be hard, and all the colour claims is that this row is worth opening.
 */
const LOW_ACCURACY = 0.35;

const HEALTH_WINDOW_DAYS = 30;

const percent = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : `${Math.round(value * 100)} %`;

/** Columns this table can be ordered by. Sorted in the browser — the whole catalogue is already here. */
type SortKey = 'name' | 'subskill' | 'minAgeYears' | 'minLevel' | 'served' | 'accuracy' | 'isEnabled';

export const ChallengesPage = () => {
  const { account } = useAuth();
  const canEdit = account?.role === 'SUPERUSER' || account?.role === 'CONTENT_EDITOR';

  const [openChallenge, setOpenChallenge] = useState<ChallengeDefinition | null>(null);

  const [skill, setSkill] = useState('');
  const [state, setState] = useState('');
  const sort = useSort<SortKey>('name');
  const { data, error, isLoading, refetch } = useQuery<ChallengeDefinition[]>({
    queryKey: ['challenges'],
    queryFn: readChallenges,
  });
  // The catalogue lists what a challenge *is*; this says what it has been doing. Merged by code so the
  // list can be sorted through by eye — the dialog is for the one row that stands out.
  const health = useQuery<ChallengeHealth>({
    queryKey: ['content-health', HEALTH_WINDOW_DAYS],
    queryFn: () => readContentHealth(HEALTH_WINDOW_DAYS),
  });

  // `content-health` as well as `challenges`: its "never served" list is built from the catalogue, so
  // disabling a task changes it too. `audit` is added by the hook, for every mutation.
  const toggle = useAdminMutation(
    ({ code, isEnabled }: { code: string; isEnabled: boolean }) =>
      toggleChallenge(code, isEnabled, isEnabled ? 'Zapnuté v back office.' : 'Vypnuté v back office.'),
    ['challenges', 'content-health'],
  );

  const all = data ?? [];
  const enabled = all.filter((challenge) => challenge.isEnabled).length;
  const healthByCode = new Map<string, ChallengeHealthRow>(
    (health.data?.challenges ?? []).map((row: ChallengeHealthRow) => [row.challengeCode, row]),
  );

  // Sorted and filtered **here**, because this table holds its whole list: there is no page 2 whose rows
  // this could be getting wrong (R0141). The two figures come from the health query, so they are read
  // through the same map the rows are drawn from.
  const VALUE: Record<SortKey, (challenge: ChallengeDefinition) => string | number | null> = {
    name: (challenge) => challenge.name,
    subskill: (challenge) => challenge.subskillName ?? challenge.subskillCode,
    minAgeYears: (challenge) => challenge.minAgeYears,
    minLevel: (challenge) => challenge.minLevel,
    served: (challenge) => healthByCode.get(challenge.code)?.issued ?? null,
    accuracy: (challenge) => healthByCode.get(challenge.code)?.accuracy ?? null,
    isEnabled: (challenge) => (challenge.isEnabled ? 1 : 0),
  };
  const items = sortRows(
    all.filter(
      (challenge: ChallengeDefinition) =>
        (skill === '' || challenge.skillCode === skill) &&
        (state === '' || String(challenge.isEnabled) === state),
    ),
    sort.direction,
    VALUE[sort.key],
  );
  const skills = [...new Set(all.map((challenge: ChallengeDefinition) => challenge.skillCode))].sort();

  return (
    <>
      <PageHeader
        title="Katalóg úloh"
        subtitle="Úlohy sú definované v kóde, takže tento zoznam sa mení len s novým nasadením. Čísla vpravo pokrývajú posledných 30 dní; „Otvoriť“ ukáže rozpad podľa úrovne, vývoj v čase a porovnanie toho, čo definícia sľubuje, so skutočnosťou. Vypnutie zabráni zaradeniu úlohy do nových tréningov; už otvorené úlohy zostanú riešiteľné."
        action={
          items.length > 0 ? (
            <Chip
              label={`${enabled} z ${items.length} zapnutých`}
              color={enabled === items.length ? 'success' : 'warning'}
              variant="outlined"
            />
          ) : undefined
        }
      />

      <ErrorNotice error={error} onRetry={() => void refetch()} />
      {/* A mutation has nothing to refetch — trying again means pressing the switch again. */}
      <ErrorNotice error={toggle.error} />
      <ErrorNotice error={health.error} onRetry={() => void health.refetch()} />

      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            select
            label="Schopnosť"
            value={skill}
            onChange={(event) => setSkill(event.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">všetky</MenuItem>
            {skills.map((one: string) => (
              <MenuItem key={one} value={one}>
                {one}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Zapnutá"
            value={state}
            onChange={(event) => setState(event.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">všetky</MenuItem>
            <MenuItem value="true">zapnuté</MenuItem>
            <MenuItem value="false">vypnuté</MenuItem>
          </TextField>
        </TableFilters>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="name">
                  Úloha
                </SortableCell>
                <SortableCell sort={sort} column="subskill">
                  Schopnosť
                </SortableCell>
                <SortableCell sort={sort} column="minAgeYears" align="center">
                  Vek
                </SortableCell>
                <SortableCell sort={sort} column="minLevel" align="center">
                  Úrovne
                </SortableCell>
                <SortableCell sort={sort} column="served" align="right">
                  Nasadení
                </SortableCell>
                <SortableCell sort={sort} column="accuracy" align="right">
                  Úspešnosť
                </SortableCell>
                {/* A button. There is nothing to order by. */}
                <TableCell align="right">Štatistika</TableCell>
                <SortableCell sort={sort} column="isEnabled" align="right">
                  Zapnutá
                </SortableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? <TableSkeletonRows columns={7} rows={8} /> : null}
              {!isLoading &&
                items.map((challenge: ChallengeDefinition) => (
                  <TableRow key={challenge.code} hover sx={{ opacity: challenge.isEnabled ? 1 : 0.55 }}>
                    <TableCell sx={{ maxWidth: 420, py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {challenge.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: 'text.disabled',
                          fontFamily: 'ui-monospace, monospace',
                        }}
                      >
                        {challenge.code}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5, lineHeight: 1.45 }}
                      >
                        {challenge.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={SKILL_LABEL[challenge.skillCode] ?? challenge.skillCode}
                        variant="outlined"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {challenge.subskillCode}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {challenge.minAgeYears}–{challenge.maxAgeYears}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {challenge.minLevel}–{challenge.maxLevel}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        color: healthByCode.get(challenge.code) ? 'text.primary' : 'text.disabled',
                      }}
                    >
                      {healthByCode.get(challenge.code)?.issued ?? 0}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight:
                          (healthByCode.get(challenge.code)?.accuracy ?? 1) < LOW_ACCURACY &&
                          healthByCode.get(challenge.code) !== undefined
                            ? 600
                            : 400,
                        color:
                          healthByCode.get(challenge.code) === undefined
                            ? 'text.disabled'
                            : (healthByCode.get(challenge.code)?.accuracy ?? 1) < LOW_ACCURACY
                              ? 'warning.main'
                              : 'text.primary',
                      }}
                    >
                      {healthByCode.get(challenge.code) === undefined
                        ? '—'
                        : percent(healthByCode.get(challenge.code)?.accuracy)}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => setOpenChallenge(challenge)}>
                        Otvoriť
                      </Button>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={canEdit ? '' : 'Vaša rola môže katalóg iba čítať.'}>
                        <Box component="span">
                          <Switch
                            size="small"
                            checked={challenge.isEnabled}
                            disabled={!canEdit || toggle.isPending}
                            onChange={(event) =>
                              toggle.mutate({ code: challenge.code, isEnabled: event.target.checked })
                            }
                          />
                        </Box>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && items.length === 0 ? (
          <EmptyState
            title="Katalóg je prázdny"
            detail="To by nastať nemalo — úlohy sú registrované v kóde. Skontrolujte, či beží admin API a či sa registrácia úloh načítala."
          />
        ) : null}
      </Paper>

      <ChallengeDashboardDialog challenge={openChallenge} onClose={() => setOpenChallenge(null)} />
    </>
  );
};
